import { SocialScraperResponse } from '@/services/socialScraperService';
import { warn } from '@/utils/logger';
import Location from '@/models/location';
import type { ItinerarySlot } from '@/utils/itinerary/types';
import type { RawLocationData } from '@/utils/postTypes';
import type { CityFormData } from '@/types/city';
import type { Day } from '@/contexts/ItineraryContext';
import { getPhotoUrls } from '@/utils/photoUtils';
import { generateDayContainerId } from '@/utils/idGeneration';

/**
 * Extract address string from place address (handles both string and object formats)
 */
function extractAddress(
  place: { address: string | { full?: string; short?: string } },
  fallback?: string
): string {
  if (typeof place.address === 'string') {
    return place.address;
  }
  return place.address?.full || place.address?.short || fallback || '';
}

/**
 * Infer slot type from Google Places category
 */
function inferSlotType(category: string | null | undefined): 'eat' | 'do' | 'stay' {
  if (!category) return 'do';
  const categoryLower = category.toLowerCase();

  if (
    categoryLower.includes('restaurant') ||
    categoryLower.includes('cafe') ||
    categoryLower.includes('bar') ||
    categoryLower.includes('food') ||
    categoryLower.includes('meal') ||
    categoryLower.includes('dining')
  ) {
    return 'eat';
  }

  if (
    categoryLower.includes('lodging') ||
    categoryLower.includes('hotel') ||
    categoryLower.includes('hostel') ||
    categoryLower.includes('accommodation')
  ) {
    return 'stay';
  }

  return 'do';
}

/**
 * Transform social scraper location to itinerary slot format
 */
export function transformSocialLocationToItinerarySlot(
  location: SocialScraperResponse['locations'][0],
  dayIndex: number,
  sequenceIndex: number
): ItinerarySlot | null {
  if (!location.place) {
    warn('[socialScraperTransform] Skipping location without place data', {
      locationName: location.name,
      city: location.city,
      country: location.country,
    });
    // Skip locations without place data
    return null;
  }

  const { place } = location;
  const coords = place.coordinates;

  // Infer slot type from category
  const slotType = inferSlotType(place.category?.primary);
  
  // Extract photo URLs for image_urls (matching AI-generated itinerary format)
  // Backend now sends identifiers only (name, photo_reference), not URLs
  // Use unified utility to convert identifiers to proxy URLs
  const photoUrls = getPhotoUrls(place.photos as Array<{ name?: string; photo_reference?: string }>, 400);
  
  // Build locationData object (matching Location model structure)
  // Match the old working format (3c33c8e) that successfully saved locations
  // The backend needs the complete google_places_data structure with snake_case fields
  // Backend sends identifiers only (name, photo_reference), preserve them as-is
  const photos = place.photos;
  
  const locationData: RawLocationData = {
    google_place_id: place.placeId,
    name: place.name, // Ensure name is set (used as body in toPostLocation)
    geoposition: {
      type: 'Point',
      coordinates: [coords.lng, coords.lat], // [longitude, latitude]
    },
    google_places_data: {
      // Places / app convention: `id` + `formattedAddress` (Location getters); keep place_id / formatted_address for backend parity
      id: place.placeId,
      // Use snake_case fields matching old working format
      // This structure is what the backend expects to create/find Location documents
      place_id: place.placeId,
      name: place.name,
      formattedAddress: place.address?.full || place.address?.short || '',
      formatted_address: place.address?.full || place.address?.short || '',
      geometry: {
        location: {
          lat: coords.lat,
          lng: coords.lng,
        },
      },
      types: place.category?.all || [],
      rating: place.rating?.score || null,
      user_ratings_total: place.rating?.totalReviews || null,
      photos: photos,
    },
    a3_intl_address: place.address?.full || place.address?.short || '', // Ensure a3_intl_address is set
    phone: place.contact.phone || undefined,
    website: place.contact.website || undefined,
    // GT-derived fields (defaults, can be enriched later)
    gt_category: slotType,
    gt_status: place.status || 'active',
    // Add photos array directly on locationData for easier access
    // Store photo identifiers (name, photo_reference) for localStorage serialization
    // Backend sends identifiers only, so preserve them (they serialize better than URLs)
    photos: place.photos.map((photo: { name?: string; photo_reference?: string; author?: unknown }) => ({
      name: photo.name,
      photo_reference: photo.photo_reference,
      author: photo.author,
    })),
    // Add image_urls to locationData (matching AI-generated itinerary format)
    // This ensures toPostLocation can find image_urls in locationDataTyped.image_urls
    // Store proxy URLs here (computed from identifiers)
    image_urls: photoUrls,
    // Additional fields that may be used elsewhere (via index signature)
    category_name: place.category?.primaryDisplay || 'Place',
    google_rating: place.rating?.score || undefined,
    city: place.address?.city || location.city || '',
    country: place.address?.country || location.country || '',
  } as RawLocationData;

  // Build itinerary slot object
  const slot = {
    dayIndex,
    sequenceIndex,
    slotType,
    locationData,
    location: {
      lat: coords.lat,
      lng: coords.lng,
    },
    name: place.name,
    title: place.name, // PlaceCard uses title prop
    placeId: place.placeId,
    isUserChosen: false,
    notes: '', // Empty notes to show "Add note" placeholder (like AI-generated itinerary)
    // Add fields required for PlaceCard display - match AI-generated itinerary defaults
    // No default time — only set when the user picks one
    timeRange: undefined,
    time: undefined,
    budget: undefined, // No default cost - let user add if needed
    cost: undefined,
    address: extractAddress(place, locationData.a3_intl_address),
    // Extract image - use first converted proxy URL from image_urls (matching AI-generated itinerary)
    image: photoUrls.length > 0 ? photoUrls[0] : null,
    // Add image_urls array with converted proxy URLs (matching AI-generated itinerary format)
    image_urls: photoUrls,
  };
  
  return slot;
}

/**
 * Return type for transformScrapedDataToItinerary
 */
export interface TransformedItineraryData {
  itineraryData: ItinerarySlot[];
  formData: {
    city: CityFormData | null;
    days: number;
    fromDate?: Date;
    toDate?: Date;
  };
  timelineData: Day[];
}

/**
 * Transform all scraped locations to itinerary format
 * Groups locations by day (all on day 0 for now, can be enhanced later)
 */
export function transformScrapedDataToItinerary(
  response: SocialScraperResponse,
  days: number = 1
): TransformedItineraryData {
  const locations = response.locations.filter((loc) => loc.place !== null);

  // Determine city from first location or use a default
  const primaryCity = locations[0]?.place?.address?.city || 'Unknown City';
  const primaryCountry = locations[0]?.place?.address?.country || 'Unknown Country';

  // Build itinerary slots (all on day 0 for now)
  const itineraryData = locations
    .map((location, index) =>
      transformSocialLocationToItinerarySlot(location, 0, index)
    )
    .filter((slot) => slot !== null);

  // Build form data (simplified - can be enhanced)
  const formData = {
    city: {
      city: primaryCity,
      country: primaryCountry,
      // You may need to fetch full city data from backend
    },
    days,
    // Optionally add dates if needed
  };

  // Build timeline data structure
  const timelineData: Day[] = [
    {
      containerId: generateDayContainerId(),
      date: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      index: 0,
      places: itineraryData.map((slot, seqIndex) => ({
        id: slot.placeId || `slot-${seqIndex}`,
        name: slot.name,
        location: slot.locationData,
        slotType: slot.slotType,
        sequenceIndex: seqIndex,
        dayIndex: 0,
      })),
    },
  ];

  return {
    itineraryData,
    formData,
    timelineData,
  };
}

/**
 * Convert a single social-scraper location row into a {@link Location} for the Add-place picker
 * (same underlying transform as Inspo → Trip, reused for list batch add).
 */
export function socialScraperEntryToLocation(
  entry: SocialScraperResponse['locations'][0]
): Location | null {
  const slot = transformSocialLocationToItinerarySlot(entry, 0, 0);
  if (!slot?.placeId || !slot.locationData) return null;
  const ld = slot.locationData as Record<string, unknown>;
  return new Location({
    ...ld,
    _id: String(slot.placeId),
    name: slot.name,
    google_place_id: slot.placeId,
  });
}

